import React, { ChangeEvent, PureComponent } from 'react';
import isNumber from 'lodash/isNumber';
import Checkbox from '@mui/material/Checkbox';
import TableSortLabel from '@mui/material/TableSortLabel';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import {
  OndemandVideo,
  Videocam as VideoCam,
  Photo,
  Audiotrack as Audio,
  Description,
} from '@mui/icons-material';
import { faCut } from '@fortawesome/free-solid-svg-icons';
import MomentTz from 'moment-timezone';
import moment from 'moment';
import { v4 } from 'uuid';
import { LocalizationLabels, UiSection } from '../../../enums';
import FilterPanel from '../Filter/component';
import { FaIcon, TagArrowIcon } from '../../Shared/Icons';
import {
  AddTagButton,
  ResultsTableContainer,
  ThumbnailImage,
  ThumbnailDiv,
  ResultHeaderThumbnailCell,
  IconCard,
  ResultsTable,
  ResultsTableBody,
  ResultsTableRow,
  ResultsTableHeaderRow,
  ResultsCheckboxCell,
  ResultsThumbnailCell,
  ResultsRowTitleCell,
  ResultsHeaderCell,
  ResultsRowCell,
  ResultsRowFixedCell,
  ResultHeaderTitleCell,
  ResultHeaderTitleExpandedCell,
  ResultsTableHead,
  ResultsDateCell,
  PlaceholderImage,
  ResultsTeamCell,
  ExchangeThumbnailImage,
  ExchangeThumbnailContainer,
  ResultsRowTitleExpandedCell,
  AssetTagsList,
  TagChip,
  AssetTagsContainer,
  StyledTableSortLabel,
} from './styles';
import {
  IProps, IState, IDispatchProps, IStateProps, IColumn,
} from './interfaces';
import {
  ISearchResultsLocaleLookupKeys,
  LocaleKeyTree,
} from '../../../localization';
import { resetPublishedAssets } from '../../../state/publishAssets';
import { IAppState } from '../../../state/reducers';
import { Product } from '../../../state/product';
import {
  queryGamesAction,
  updateSearchHitTags,
  updateSearchQueryParams,
} from '../../../state/search';
import {
  openWarningDialog,
  triggeredByNewSelection as _triggeredByNewSelection,
  returnToEdit as _returnToEdit,
  resetTriggeredByNewSelection,
} from '../../AssetDetails/state/warningDialog';
import { IconType, Colors } from '../../../constants';
import { AssetHit, AssetKeys, IOrgTagIds } from '../../../api/vault/searchEngine/interfaces';
import {
  selectIsPublishPermitted,
  selectIsUserVaultSuperAdmin,
  selectIsUserExchangeAdmin,
  selectIsUserExchangeVendor,
  selectIsAssetDownloadPermitted,
  selectUserOrgIds,
} from '../../../state/user/userOrgRole';
import { VAULT_MEDIA_TYPE } from '../../../constants/vault';
import { getLocale } from '../../../state/localization';
import AddTagsMenu from '../TagsMenu/component';
import {
  loadAssetTags, untagAsset,
} from '../../../state/tags';

const ExchangeFields = ['exchangeQualifier', 'teamGameNumber'];

const phaseString = 'Phase';

class SearchTable extends PureComponent<IProps, IState> {
  private static readonly resultsTableID = `${UiSection.SearchResults}.Table`;
  private static readonly rowTitle = 'title';
  private static readonly tableBodyId = 'tableBody';
  private static readonly rowThumbNailURL = 'thumbnailURL';
  private static readonly checkBox = 'checkbox';
  private static readonly DESC = 'desc';
  private static readonly ASC = 'asc';
  private scrollToTop: React.RefObject<HTMLTableSectionElement>;

  constructor(props: IProps) {
    super(props);
    const { t } = props;
    this.scrollToTop = React.createRef();
    this.columns = [
      { name: 'thumbnailImageUrl', value: t(this.localeLookupKeys.resultsTableKeys.headerThumbNailUrl) },
      { name: 'title', value: t(this.localeLookupKeys.resultsTableKeys.headerTitle) },
      { name: 'orgTagIds', value: t(this.localeLookupKeys.resultsTableKeys.headerOrgTagIds) },
      { name: 'gameDate', value: t(this.localeLookupKeys.resultsTableKeys.headerDate) },
      { name: 'sport', value: t(this.localeLookupKeys.resultsTableKeys.headerSport) },
      { name: 'awayTeam', value: t(this.localeLookupKeys.resultsTableKeys.headerAwayTeam) },
      { name: 'homeTeam', value: t(this.localeLookupKeys.resultsTableKeys.headerHomeTeam) },
      { name: 'videoType', value: t(this.localeLookupKeys.resultsTableKeys.headerVideoType) },
      { name: 'owner', value: t(this.localeLookupKeys.resultsTableKeys.headerOwner) },
      { name: 'exchangeQualifier', value: t(this.localeLookupKeys.resultsTableKeys.headerQualifier) },
      { name: 'teamGameNumber', value: t(this.localeLookupKeys.resultsTableKeys.headerGameNumber) },
      { name: 'createdDate', value: t(this.localeLookupKeys.resultsTableKeys.headerCreatedDate) },
    ];
  }

  state: Readonly<IState> = {
    selectedClipperAssetIndex: null,
    selectedAssetIndexCopy: null,
    addTagsAnchorEl: null,
  };

  private columns: IColumn[] = [];

  private readonly localeLookupKeys: Readonly<ISearchResultsLocaleLookupKeys> = LocaleKeyTree.searchResults;

  private getAssetTypePath = (assetType: string) => {
    if (assetType === IconType.LIVE_VIDEO) {
      return <OndemandVideo />;
    }
    if (assetType === IconType.VIDEO) {
      return <VideoCam />;
    }
    if (assetType === IconType.IMAGE) {
      return <Photo />;
    }
    if (assetType === IconType.AUDIO) {
      return <Audio />;
    }
    if (assetType === IconType.DOCUMENT) {
      return <Description />;
    }
    if (assetType === IconType.EXCHANGE) {
      return <Description />;
    }
    if (assetType === IconType.CLIP) {
      return <FaIcon icon={faCut} />;
    }
    return null;
  };

  private renderThumbnailImage = (srcURL: string, assetType: string, photoRotation?: number) => {
    const iconType = this.getAssetTypePath(assetType);

    return (
      <ThumbnailDiv>
        {/* TODO: i18n */}
        {srcURL
          ? ThumbnailImage(srcURL, photoRotation)
          : <PlaceholderImage alt="No Placeholder Image" />}
        <IconCard className="material-icons">
          {iconType}
        </IconCard>
      </ThumbnailDiv>
    );
  };

  private renderExchangeThumbnailImage = (homeSrcURL: string, awaySrcURL: string, assetType: string) => {
    const iconType = this.getAssetTypePath(assetType);

    return (
      <ThumbnailDiv>
        {/* TODO: i18n */}
        {homeSrcURL && awaySrcURL
          ? (
            <ExchangeThumbnailContainer>
              <ExchangeThumbnailImage src={homeSrcURL} alt="" />
              <ExchangeThumbnailImage src={awaySrcURL} alt="" />
            </ExchangeThumbnailContainer>
          ) : <PlaceholderImage alt="No Placeholder Image" />}
        <IconCard className="material-icons">
          {iconType}
        </IconCard>
      </ThumbnailDiv>
    );
  };

  private handleRequestSort = (column: string) => {
    const {
      queryParams: {
        sortField,
        sortOrder,
      },
      queryGames,
      updateQueryParams,
    } = this.props;
    const { ASC, DESC } = SearchTable;
    const isDesc = sortField === column && sortOrder === DESC;

    updateQueryParams({
      sortField: column,
      sortOrder: isDesc ? ASC : DESC,
    });

    queryGames();
  };

  handleAllChecked = (e: ChangeEvent<HTMLInputElement>) => {
    const { rows, assetIdSelection } = this.props;
    if (rows) {
      (!e.target.checked)
        ? assetIdSelection.deselectAll()
        : assetIdSelection.selectAll(...(rows.map((asset) => asset._id)));
      this.handleSelectedAssets();
      this.forceUpdate();
    }
  };

  handleCheckBoxClick = (e: React.MouseEvent<HTMLElement>, asset: AssetHit) => {
    // When checkbox is clicked and there is a selected item already
    // ignore the click row event, as we just want to select the asset (not load in the details, multi-select)
    const { assetIdSelection } = this.props;
    const selectedAssets = assetIdSelection.getAll();
    if (selectedAssets && selectedAssets.length > 0) e.stopPropagation();
  };

  handleCheckBoxChange = (e: ChangeEvent<HTMLInputElement>, asset: AssetHit, index: number) => {
    // If shift key is held during selection and checkbox is pressed (selectedAssets check)
    // we handle the shift selection here
    const { assetIdSelection } = this.props;
    const nativeEvent = e.nativeEvent as MouseEvent;
    const selectedAssets = assetIdSelection.getAll();
    if (nativeEvent && nativeEvent.shiftKey && selectedAssets && selectedAssets.length > 0) {
      this.handleShiftSelection(index);
    } else {
      assetIdSelection.toggle(asset._id);
    }
    this.handleSelectedAssets();
    this.forceUpdate();
  };

  handleClickRow = async (index: number, event: React.MouseEvent<HTMLElement>) => {
    const { isEditingAsset, handleClickRow } = this.props;
    if (isEditingAsset) {
      /* eslint-disable-next-line no-shadow */
      const { triggeredByNewSelection, openWarningDialog } = this.props;

      await triggeredByNewSelection();
      openWarningDialog();
      this.setState({
        selectedAssetIndexCopy: index,
      });
    } else {
      this.setState({
        selectedClipperAssetIndex: index,
      });
      handleClickRow(index, event);

      const { assetIdSelection, rows } = this.props;
      if (event && event.shiftKey) {
        this.handleShiftSelection(index);
      } else if (event && (event.metaKey || event.ctrlKey)) {
        this.handleCmdCtrlSelection(index);
      } else if (index && event) {
        const isChecked = assetIdSelection.isSelected(rows[index]._id);
        if (!isChecked) {
          // This is assuming it is a row click, and the checkbox click did not trigger this
          // (handleCheckBoxClick func to handle that). We deselect selected rows and view the single asset
          assetIdSelection.deselect(...(rows.map((asset) => asset._id)));
          this.handleSelectedAssets();
          this.forceUpdate();
        }
      }
    }
  };

  private handleShiftSelection = (selectedIndex: number) => {
    const { selectedClipperAssetIndex: lastSelectedIndex } = this.state;
    const { assetIdSelection, rows } = this.props;

    if (lastSelectedIndex !== null && rows && isNumber(selectedIndex)) {
      const startSliceIndex = Math.min(selectedIndex, lastSelectedIndex);
      const endSliceIndex = (Math.max(selectedIndex, lastSelectedIndex) + 1);

      const cloneRowsArray = rows.slice();
      const selectedAssetsRange = cloneRowsArray.map((asset) => asset._id).slice(startSliceIndex, endSliceIndex);
      const isChecked = assetIdSelection.isSelected(rows[selectedIndex]._id);

      if (isChecked) assetIdSelection.deselect(...selectedAssetsRange);
      else assetIdSelection.select(...selectedAssetsRange);
      this.handleSelectedAssets();
      this.forceUpdate();
    }
  };

  private handleCmdCtrlSelection = (selectedIndex: number) => {
    const { assetIdSelection, rows } = this.props;
    const assetRow = rows[selectedIndex];
    if (assetIdSelection.isSelected(assetRow._id)) assetIdSelection.deselect(assetRow._id);
    else assetIdSelection.select(assetRow._id);
    this.handleSelectedAssets();
    this.forceUpdate();
  };

  handleSelectedAssets = () => {
    const { assetIdSelection, handleSelectedAssets, rows } = this.props;
    const assetIds = assetIdSelection.getAll();
    const assets = rows.filter((row) => assetIds.includes(row._id));
    handleSelectedAssets(assetIds, assets);
  };

  private createAssetCheckBox = (asset: AssetHit, index: number) => {
    const { assetIdSelection } = this.props;
    const checkBoxId: string = `${asset._id}_${SearchTable.checkBox} `;
    return (
      <Checkbox
        id={checkBoxId}
        checked={assetIdSelection.isSelected(asset._id) || this.isAssetSelected(asset)}
        onChange={(e) => this.handleCheckBoxChange(e, asset, index)}
        onClick={(e) => this.handleCheckBoxClick(e, asset)}
        color="secondary"
      />
    );
  };

  private createSelectAllCheckBox = (checkboxId: string, shouldBeDisabled: boolean) => {
    const { selectedAssetIds, rows } = this.props;
    const checkBoxId: string = `${checkboxId}_${SearchTable.checkBox} `;
    let checked = false;
    if (selectedAssetIds?.length && rows?.length && selectedAssetIds?.length > 0) {
      checked = selectedAssetIds.length === rows.length;
    }
    return (
      <Checkbox
        id={checkBoxId}
        checked={checked}
        disabled={shouldBeDisabled}
        onChange={this.handleAllChecked}
        color="secondary"
      />
    );
  };

  private convertGameDate = (date: string) => {
    if (date && new Date(date).toString() !== 'Invalid Date') {
      const formattedDate = MomentTz.utc(date).format('ll');
      return formattedDate;
    }
    return null;
  };

  private convertDate = (date: string) => {
    if (date && new Date(date).toString() !== 'Invalid Date') {
      const formattedDate = moment(date).format('ll');
      return formattedDate;
    }
    return null;
  };

  renderTeamRows = (teamName: string, id: string) => {
    if (teamName && teamName.length > 25) {
      return (
        <Tooltip id={`${id}.tooltip`} title={teamName} placement="top">
          <ResultsTeamCell id={id}>{teamName}</ResultsTeamCell>
        </Tooltip>
      );
    }
    return (
      <ResultsRowCell id={id} align="left">{teamName}</ResultsRowCell>
    );
  };

  handleRemoveTag = (assetId: string, assetTags: IOrgTagIds[], tagToRemove: IOrgTagIds) => async () => {
    const {
      removeTagFromAsset, loadCurrentAssetTags,
    } = this.props;
    const tags = assetTags ? [...assetTags] : [];
    loadCurrentAssetTags(tags);
    const updatedTags = tags.filter((oldTag) => oldTag._id !== tagToRemove._id);
    removeTagFromAsset([assetId], tagToRemove, updatedTags);
  };

  filterCurrentOrgTags = (tags: IOrgTagIds[]) => {
    const { userOrgIds } = this.props;
    const [userOrgId] = userOrgIds;
    const currentOrgTags = tags.filter((tag) => tag.orgId === userOrgId);
    return currentOrgTags;
  };

  renderAssetTags = (assetId: string, assetTags: IOrgTagIds[]) => {
    const tags = assetTags ? [...assetTags] : [];
    const currentAssetTags = this.filterCurrentOrgTags(tags);
    if (currentAssetTags !== undefined && currentAssetTags.length > 0) {
      return (
        currentAssetTags.map((tag: IOrgTagIds) => (
          <TagChip
            key={v4()}
            size="small"
            label={tag.name}
            onDelete={this.handleRemoveTag(assetId, currentAssetTags, tag)}
          />
        ))
      );
    }
    return null;
  };

  private renderTableSortLabel = (column: IColumn, phaseString: string, phaseLabel: string, filters: any, noRows: boolean, sortField: string, sortDirection: 'desc' | 'asc') => {
    const { name } = column;
    let { value } = column;
    if (value === phaseString) value = phaseLabel;
    const sortLabelID = `${UiSection.SearchResults}.${name}.sort`;
    const hasFilter = column.name in filters;

    return (
      <StyledTableSortLabel
        disabled={noRows}
        id={sortLabelID}
        active={sortField === name}
        direction={sortDirection}
        onClick={(e) => this.handleRequestSort(name)}
        style={{ color: hasFilter ? Colors.RED_1 : null }}
      >
        {value}
      </StyledTableSortLabel>
    );
  };

  private headerLabelGenerator = (withOwner: boolean) => {
    const {
      filters,
      rows,
      isAssetDownloadPermitted,
      isPublishPermitted,
      queryParams: {
        sortOrder,
        sortField,
      },
      isExchange,
      isMyVault,
      isVault,
      phaseLabel,
    } = this.props;

    const noRows = !rows || rows.length === 0;
    const selectAllCheckBox = this.createSelectAllCheckBox(SearchTable.resultsTableID, noRows);

    return (
      <ResultsTableHeaderRow>
        <ResultsCheckboxCell padding="checkbox" align="left">
          {(isPublishPermitted || isAssetDownloadPermitted) && selectAllCheckBox}
        </ResultsCheckboxCell>
        {this.columns.map((column, i) => {
          const { name } = column;
          const headerID = `${UiSection.SearchResults}.${name}`;

          const sortLabel = this.renderTableSortLabel(column, phaseString, phaseLabel, filters, noRows, sortField, sortOrder || SearchTable.ASC);

          if (name === SearchTable.rowThumbNailURL) {
            return (
              <ResultHeaderThumbnailCell key={i} id={headerID} align="left" sortDirection={sortField === name ? sortOrder : false}>
                {sortLabel}
              </ResultHeaderThumbnailCell>
            );
          }

          if (name === SearchTable.rowTitle) {
            if (isVault) {
              const orgTagIDsColumn = this.columns.find((col) => col.name === AssetKeys.OrgTagIds);
              const tagsSortLabel = orgTagIDsColumn
                ? this.renderTableSortLabel(orgTagIDsColumn, phaseString, phaseLabel, filters, noRows, sortField, sortOrder || SearchTable.ASC)
                : null;
              return (
                <ResultHeaderTitleExpandedCell key={name} id={headerID} align="left" sortDirection={sortField === name ? sortOrder : false} style={{ display: 'flex', gap: '16px' }}>
                  {sortLabel}
                  {tagsSortLabel}
                </ResultHeaderTitleExpandedCell>
              );
            }
            return (
              <ResultHeaderTitleCell key={name} id={headerID} align="left" sortDirection={sortField === name ? sortOrder : false}>
                {sortLabel}
              </ResultHeaderTitleCell>
            );
          }

          if (name === AssetKeys.VideoType && (isExchange || isMyVault)) return null;
          if (!isExchange && ExchangeFields.includes(name)) return null;
          if (name === AssetKeys.Owner && !withOwner) return null;
          if (name === AssetKeys.OrgTagIds) return null;

          return (
            <ResultsHeaderCell key={name} id={headerID} align="left" sortDirection={sortField === name ? sortOrder : false}>
              {sortLabel}
            </ResultsHeaderCell>
          );
        })}
      </ResultsTableHeaderRow>
    );
  };

  private rowGenerator = (items: AssetHit[], withOwner: boolean) => {
    const {
      isAssetDownloadPermitted,
      isPublishPermitted,
      securityProfileOptions,
      isExchange,
      isMyVault,
      isVault,
      teamIcons,
    } = this.props;
    const { selectedClipperAssetIndex, addTagsAnchorEl } = this.state;

    const tagMenuIcon: JSX.Element = (
      <TagArrowIcon />
    );

    return items.map((item, i: number) => {
      const asset = item._source;
      let { gameDate } = asset;
      if (asset.gameDate) {
        gameDate = this.convertGameDate(asset.gameDate);
      }
      const createdDate = (asset.createdDate) ? this.convertDate(asset.createdDate) : asset.createdDate;
      const ownerOption = securityProfileOptions.find(({ _id }) => _id === asset.owner);

      let homeTeamIcon: string;
      let awayTeamIcon: string;
      if (isExchange && teamIcons) {
        homeTeamIcon = teamIcons[asset.homeTeamId];
        awayTeamIcon = teamIcons[asset.awayTeamId];
      }

      const tableRowChildren = (
        <>
          <ResultsCheckboxCell padding="checkbox" align="left">
            {(isPublishPermitted || isAssetDownloadPermitted) && this.createAssetCheckBox(item, i)}
          </ResultsCheckboxCell>
          {(isExchange)
            ? (
              <ResultsThumbnailCell id={`${item._id}.${AssetKeys.AssetType}`}>
                {this.renderExchangeThumbnailImage(homeTeamIcon, awayTeamIcon, asset.type)}
              </ResultsThumbnailCell>
            )
            : <ResultsThumbnailCell id={`${item._id}.${AssetKeys.AssetType}`}>{this.renderThumbnailImage(asset.thumbnailImageUrl, asset.type, asset.photoRotation)} </ResultsThumbnailCell>}
          {isVault // Expanded title row on vault for tags filter
            ? (
              <ResultsRowTitleExpandedCell id={`${item._id}.${AssetKeys.Title}`} align="left">
                {asset.title}
                <AssetTagsContainer onClick={(e) => e.stopPropagation()}>
                  <AssetTagsList>
                    {this.renderAssetTags(item._id, item._source.orgTagIds)}
                  </AssetTagsList>
                  <AddTagsMenu
                    addTagsAnchorEl={addTagsAnchorEl}
                    assetTags={item._source.orgTagIds}
                    assetId={[item._id]}
                    addTagButton={AddTagButton}
                    buttonDisabled={false}
                    buttonText="Add Tags&nbsp;"
                    buttonIcon={tagMenuIcon}
                  />
                </AssetTagsContainer>
              </ResultsRowTitleExpandedCell>
            )
            : (<ResultsRowTitleCell id={`${item._id}.${AssetKeys.Title}`} align="left">{asset.title}</ResultsRowTitleCell>)}
          <ResultsDateCell id={`${item._id}.${AssetKeys.GameDate}`} align="left">{gameDate} </ResultsDateCell>
          <ResultsRowCell id={`${item._id}.${AssetKeys.Sport}`} align="left">{asset.sport}</ResultsRowCell>
          {this.renderTeamRows(asset.awayTeam, `${item._id}.${AssetKeys.AwayTeam}`)}
          {this.renderTeamRows(asset.homeTeam, `${item._id}.${AssetKeys.HomeTeam}`)}
          {(!isExchange && !isMyVault) && (
            <ResultsRowCell id={`${item._id}.${AssetKeys.VideoType}`} align="left">{asset.videoType}</ResultsRowCell>
          )}
          {isExchange && (
            <>
              <ResultsRowFixedCell id={`${item._id}.${AssetKeys.Qualifier}`} align="left">{asset.exchangeQualifier}</ResultsRowFixedCell>
              <ResultsRowFixedCell id={`${item._id}.${AssetKeys.Qualifier}`} align="left">{asset.teamGameNumber}</ResultsRowFixedCell>
            </>
          )}
          {withOwner && (
            <ResultsRowCell id={`${item._id}.${AssetKeys.Owner}`} align="left">{ownerOption ? ownerOption.label : asset.owner}</ResultsRowCell>
          )}
          <ResultsDateCell id={`${item._id}.${AssetKeys.CreatedDate}`} align="left">{createdDate} </ResultsDateCell>
        </>
      );

      return (
        <ResultsTableRow
          game-title={asset.title}
          key={item._id}
          id={item._id}
          onClick={(event) => this.handleClickRow(i, event)}
          $isFocused={isNumber(selectedClipperAssetIndex) && i === selectedClipperAssetIndex}
          $isSelected={this.isAssetSelected(item)}
        >
          {tableRowChildren}
        </ResultsTableRow>
      );
    });
  };

  private isAssetSelected = (item: AssetHit) => {
    const { assetIdSelection } = this.props;
    return assetIdSelection.getAll().includes(item._id);
  };

  componentDidUpdate(prevProps: any) {
    const {
      product,
      queryParams,
      shouldPublishedAssetsReset,
      resetSelectedAssets,
      handleSelectedAssets,
      warningDialog: {
        returnToEdit,
        triggeredByNewSelection,
      },
      assetIdSelection,
      handleClickRow,
    } = this.props;
    if (shouldPublishedAssetsReset || product !== prevProps.product) {
      assetIdSelection.deselectAll();
      resetSelectedAssets(false);
    }
    if (prevProps.queryParams !== queryParams) {
      this.scrollToTop.current.scrollTop = 0;
      handleSelectedAssets([], []);
      assetIdSelection.deselectAll();
      this.handleClickRow(null, null);
    }
    if (prevProps.returnToEdit !== returnToEdit && triggeredByNewSelection && !returnToEdit) {
      /* eslint-disable-next-line no-shadow */
      const { resetTriggeredByNewSelection, returnToEdit } = this.props;
      /* eslint-disable-next-line react/no-access-state-in-setstate */
      const index = this.state.selectedAssetIndexCopy;
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({
        selectedClipperAssetIndex: index,
      });
      handleClickRow(index, null);

      resetTriggeredByNewSelection();
      returnToEdit();
    }
  }

  render() {
    const {
      rows,
      isUserSuperAdmin,
      isUserExchangeAdmin,
      isExchange,
    } = this.props;

    const {
      headerLabelGenerator,
      rowGenerator,
      scrollToTop,
    } = this;

    const userCanViewOwner = (isUserSuperAdmin || isUserExchangeAdmin) && !isExchange;

    return (
      <ResultsTableContainer>
        <ResultsTable>
          <ResultsTableHead>
            {headerLabelGenerator(userCanViewOwner)}
            <FilterPanel withOwner={userCanViewOwner} />
          </ResultsTableHead>
          <ResultsTableBody id={SearchTable.tableBodyId} ref={scrollToTop}>
            {rowGenerator(rows, userCanViewOwner)}
          </ResultsTableBody>
        </ResultsTable>
      </ResultsTableContainer>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => {
  // VA-1660: ensure vaultEditor permissions only available to exchangeVendors with MyVault product
  const isMyVault = state.product.target === Product.MyVault;
  const isUserExchangeVendor = selectIsUserExchangeVendor(state);
  let isPublishPermitted = selectIsPublishPermitted(state);
  if (isUserExchangeVendor && !isMyVault) {
    isPublishPermitted = false;
  }
  return {
    queryParams: state.search.queryParams,
    shouldPublishedAssetsReset: state.publishSelectedAssetsToEndPoints.resetAssetsSelected,
    filters: state.filterAssets.filters,
    isEditingAsset: state.editMetadata.isEditable,
    warningDialog: state.warningDialog,
    isPublishPermitted,
    isAssetDownloadPermitted: selectIsAssetDownloadPermitted(state),
    isUserSuperAdmin: selectIsUserVaultSuperAdmin(state),
    isUserExchangeAdmin: selectIsUserExchangeAdmin(state),
    userOrgIds: selectUserOrgIds(state),
    product: state.product?.target,
    isExchange: state.product?.target === Product.Exchange,
    isMyVault,
    isVault: state.product?.target === Product.Vault,
    securityProfileOptions: state.securityProfiles?.securityProfileOptions ?? [],
    teamIcons: state.search?.teamIcons,
    phaseLabel: getLocale(state, LocalizationLabels.ExchangePhase),
  };
};

const mapDispatchToProps: IDispatchProps = {
  queryGames: queryGamesAction,
  updateQueryParams: updateSearchQueryParams,
  resetSelectedAssets: resetPublishedAssets,
  openWarningDialog,
  triggeredByNewSelection: _triggeredByNewSelection,
  resetTriggeredByNewSelection,
  returnToEdit: _returnToEdit,
  removeTagFromAsset: untagAsset,
  loadCurrentAssetTags: loadAssetTags,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(SearchTable));
